<template>
    <div class="generate-brief-container">
        <div class="summary-section">
            <div class="summary-card-container">
                <pendo-card
                    class="summary-card"
                    title="Preview brief">
                    <div class="summary-card-content">
                        Use the sample to review layout and content.
                        <br>
                        <br>
                        If the data or format doesn't look right, you can go back and update your configuration before
                        generating the final brief.
                    </div>
                </pendo-card>
            </div>
        </div>
        <div class="preview-section">
            <div class="brief-preview-container">
                <div class="preview-container">
                    <pendo-status-overlay
                        v-if="isLoading"
                        class="status-overlay"
                        state="loading" />
                    <div
                        v-show="!isLoading"
                        ref="previewContainer"
                        class="docx-preview"
                        style="width: 100%; height: 100%;" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoCard, PendoStatusOverlay } from '@pendo/components';
import { renderAsync } from 'docx-preview';

export default {
    name: 'GenerateBrief',
    components: {
        PendoCard,
        PendoStatusOverlay,
    },
    props: {
        impactBriefPreview: {
            type: Blob,
            default: null,
        },
    },
    emits: [],
    data () {
        return {
            isLoading: true,
        };
    },
    watch: {
        impactBriefPreview: {
            immediate: true,
            async handler (newValue) {
                if (newValue instanceof Blob) {
                    await this.renderPreview(newValue);
                } else {
                    this.isLoading = true;
                }
            },
        },
    },
    methods: {
        async renderPreview (blob) {
            if (!blob) {
                this.isLoading = true;

                return;
            }

            try {
                this.isLoading = true;
                await this.$nextTick();

                const container = this.$refs.previewContainer;
                if (!container) {
                    this.isLoading = true;

                    return;
                }

                try {
                    await renderAsync(blob, container, null, {
                        className: 'docx',
                        inWrapper: true,
                        ignoreWidth: false,
                        ignoreHeight: false,
                        ignoreFonts: false,
                        breakPages: true,
                        experimental: false,
                        trimXmlDeclaration: true,
                        useBase64URL: false,
                        renderHeaders: true,
                        renderFooters: true,
                        renderFootnotes: true,
                        renderEndnotes: true,
                        renderComments: false,
                        renderAltChunks: true,
                        debug: true,
                    });
                    this.isLoading = false;
                } catch (renderError) {
                    this.isLoading = true;
                    throw renderError;
                }
            } catch {
                this.isLoading = true;
            }
        },
    },
};
</script>

<style lang="less" scoped>
@noGroups: false;

.generate-brief-container {
    display: grid;
    grid-template-columns: 25% 75%;
    height: 100%;
    overflow: hidden;
    background-color: var(--palette-gray-0);

    .summary-section {
        grid-column: 1;
        overflow-y: auto;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        .summary-card-container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
        }

        .summary-card-content {
            color: var(--palette-gray-100);
            font-family: Inter;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 150%;
        }
    }
    :deep(.summary-card) {
        height: 200px;
        margin: 20px;
        max-width: 450px;
        border-radius: 3px;
        border: 1px solid var(--palette-gray-40);
        overflow: hidden;
    }

    .preview-section {
        grid-column: 2;
        overflow-y: auto;
        height: 100%;
        padding: 0px 36px;
        border-left: 2px solid var(--palette-gray-40);
        background-color: var(--palette-gray-10);

        .brief-preview-container {
            display: grid;
            grid-template-rows: auto 1fr;
            height: 100%;
            border: 4px var(--palette-gray-40);
            border-radius: 4px;
            position: relative;
            height: 100%;
            overflow-y: auto;
            padding: 0;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding-top: 20px;
        }
    }
}

:deep(.docx-wrapper) {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0px;
    padding-bottom: 16px;
    background-color: var(--palette-gray-10);
    gap: 20px;

    > * {
        flex: 0 0;
        box-sizing: border-box;
        margin: 0;
    }

    > section.docx {
        margin-bottom: 0;
        box-shadow: none;
    }
}
:deep(.docx) {
    min-width: 794px;
    max-width: 794px;
    box-sizing: border-box;
    border: 2px dashed var(--palette-gray-40);
    font-family: Arial !important;
}

:deep(.pendo-loading-overlay) {
    background: var(--palette-gray-10) !important;
}

:deep(.docx *) {
    font-family: Arial !important;
    line-height: 1.2;
}

:deep(.docx p) {
    font-family: Arial !important;
}

:deep(.docx span) {
    font-family: Arial !important;
}

:deep(.docx div) {
    font-family: Arial !important;
}

:deep(.docx h1, .docx h2, .docx h3, .docx h4, .docx h5, .docx h6) {
    font-family: Arial !important;
}

:deep(.docx td, .docx th) {
    font-family: Arial !important;
}

:deep(.docx li) {
    font-family: Arial !important;
}
</style>
